import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import BackButton from 'components/BackButton/BackButton';
import { Button } from 'components/Button/Button';
import Modal from 'components/Modal/Modal';

import { useAppLanguage } from 'hooks/useAppLanguage';
import { useAppTheme } from 'hooks/useAppTheme';
import { SSOAuthProviderKey } from 'types/sso';
import appUrls from 'utils/app-urls';

import { useLinkSsoProviderMutation } from 'app/auth/api/auth-api';
import Title from 'components/Title/Title';
import { Toggle } from 'components/Toggle/Toggle';
import { useGetLoginMethodsQuery } from 'store/slices/profile-api';
import { LoginMethod } from 'types/profile';
import messages from '../messages';
import { set } from 'lodash';
import ActionNotAllowedModal from 'components/ActionNotAllowed/ActionNotAllowedModal';

const imgAppleFeatureIcon = `${appUrls?.getCDNUrl()}/assets/auth/feature-icon-apple.png`;
const imgAppleFeatureIconChecked = `${appUrls?.getCDNUrl()}/assets/auth/feature-icon-apple-checked.png`;
const imgGoogleFeatureIcon = `${appUrls?.getCDNUrl()}/assets/auth/feature-icon-google.png`;
const imgGoogleFeatureIconChecked = `${appUrls?.getCDNUrl()}/assets/auth/feature-icon-google-checked.png`;

export interface LoginMethodOption {
  key: string;
  icon?: any;
  iconWithCheckmark?: any;
  name: string;
  description?: string;
}

export const Route = createFileRoute('/_authenticated/settings/login-method')({
  component: ProfileLoginMethod,
});

function ProfileLoginMethod() {
  const { theme } = useAppTheme();
  const navigate = useNavigate();
  const language = useAppLanguage();
  const viewMessages = messages[language].loginMethod;

  const [showLinkNotAllowed, setShowLinkNotAllowed] = useState<boolean>(false);
  const [showLinkError, setShowLinkError] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [signinMethod, setSigninMethod] = useState<string>('');
  const [
    linkWithProvider,
    {
      isLoading: linkWithProviderLoading,
      error: linkWithProviderError,
      data: linkWithProviderData,
    },
  ] = useLinkSsoProviderMutation();

  const { data: loginMethodsData } = useGetLoginMethodsQuery();

  const loginMethods: LoginMethodOption[] = [
    {
      key: 'GOOGLE' as const,
      name: viewMessages.google,
      description: viewMessages.googleDescription,
      icon: imgGoogleFeatureIcon,
      iconWithCheckmark: imgGoogleFeatureIconChecked,
    },
    {
      key: 'APPLE' as const,
      name: viewMessages.apple,
      description: viewMessages.appleDescription,
      icon: imgAppleFeatureIcon,
      iconWithCheckmark: imgAppleFeatureIconChecked,
    },
  ];

  const handleLoginMethodOption = (key: string) => {
    setSigninMethod(key);
    setShowModal(true);
  };

  const PreConfirmationModalContent = () => {
    const selectedMethod = loginMethods.find(
      (lm: LoginMethodOption) => lm.key === signinMethod,
    );

    return (
      <div className="flex flex-col items-center text-center gap-4">
        <img
          src={selectedMethod?.icon}
          alt={`login-method-${selectedMethod?.key}`}
          className="w-32 mx-auto"
        />
        <div className="text-header-text text-2xl">
          {viewMessages.loginWithProvider(selectedMethod?.name as string)}
        </div>
        <div
          dangerouslySetInnerHTML={{
            __html: viewMessages.modalPreAuthDescription(
              selectedMethod?.name as string,
            ),
          }}
        />
        <div className="flex flex-col items-center justify-center">
          <Button
            loading={linkWithProviderLoading}
            className="w-2/3 my-2"
            onClick={() =>
              linkWithProvider(signinMethod as SSOAuthProviderKey)
            }>
            {viewMessages.submitText}
          </Button>
          <Button
            className="w-2/3"
            outlined
            onClick={() => setShowModal(false)}>
            {/* TODO: get current login method from profile or something */}
            {viewMessages.keepUsingLoginMethod('email')}
          </Button>
        </div>
      </div>
    );
  };

  const PostConfirmationModalContent = () => {
    const selectedMethod = loginMethods.find(
      (lm: LoginMethodOption) => lm.key === signinMethod,
    );

    return (
      <div className="flex flex-col items-center text-center gap-4">
        <img
          src={selectedMethod?.iconWithCheckmark}
          alt={`login-method-${selectedMethod?.key}-success`}
          className="w-32 mx-auto"
        />
        <div className="text-header-text text-2xl">
          {viewMessages.loginMethodUpdated}
        </div>
        <div
          dangerouslySetInnerHTML={{
            __html: viewMessages.modalPostAuthDescription(
              selectedMethod?.name as string,
            ),
          }}
        />
        <div className="">
          <Button className="w-2/3 mt-4" onClick={() => navigate({ to: '/' })}>
            {viewMessages.submitText}
          </Button>
          {/* <div>{viewMessages.secondaryText}</div> */}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full px-8">
      <BackButton className={'w-full text-left lg:bg-body-background mt-1 ml-0 pl-6 lg:mt-2 pt-2 lg:ml-0 lg:pl-0 lg:pt-0'} />
      <div className="flex flex-col items-start md:items-center w-full h-full">
        <Title title={viewMessages.title} subTitle={viewMessages.subTitle} />
        <div className="w-full md:w-11/20 cursor-pointer">
          {loginMethods.map((method: LoginMethodOption) => {
            const methodData = loginMethodsData?.find(
              (v: LoginMethod) => v.type === method.key,
            );
            return (
              <button
                key={method.key}
                type="button"
                onClick={() =>
                  methodData?.canEnable
                    ? handleLoginMethodOption(method.key)
                    : setShowLinkNotAllowed(true)
                }
                className="flex flex-row p-4 mb-2 w-full text-left text-header-text rounded-lg justify-between"
                style={{ backgroundColor: theme['blue-grey'] }}>
                <div className="flex flex-row">
                  <img
                    src={method?.icon}
                    alt={`sso-icon-${method.key}`}
                    className="w-12 h-12 mr-4 object-contain"
                  />
                  <div>
                    <div className="text-header-text">{method?.name}</div>
                    <div className="text-header-text">
                      {method?.description}
                    </div>
                  </div>
                </div>
                <div className="flex-end">
                  <Toggle
                    value={methodData?.enabled as boolean}
                    disabled={!methodData?.canEnable as boolean}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <Modal
        content={
          <div>
            {linkWithProviderData ? (
              <PostConfirmationModalContent />
            ) : (
              <PreConfirmationModalContent />
            )}
          </div>
        }
        hideModal={() => setShowModal(false)}
        show={showModal}
      />
      {showLinkNotAllowed && (
        <ActionNotAllowedModal
          isAuthenticated={true}
          title="Contact customer support to change your login method."
          onClose={() => setShowLinkNotAllowed(false)}
        />
      )}
    </div>
  );
}

export default ProfileLoginMethod;
